/**
 * MaintenanceOverlay — Vollbild-Hinweis, wenn das Backend nicht erreichbar ist
 * (z.B. während eines Deploys). Erkennung via globalem Axios-Interceptor:
 * Netzwerkfehler oder 502/503/504 → Health-Check → Overlay + Auto-Reconnect.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { RefreshCw, Wrench } from 'lucide-react';

const HEALTH_URL = `${process.env.REACT_APP_BACKEND_URL}/api/health`;
const GATEWAY_CODES = [502, 503, 504];

const checkHealth = async () => {
  try {
    const res = await fetch(HEALTH_URL, { signal: AbortSignal.timeout(5000), cache: 'no-store' });
    return res.ok;
  } catch {
    return false;
  }
};

export const MaintenanceOverlay = () => {
  const [down, setDown] = useState(false);
  const [reconnected, setReconnected] = useState(false);
  const checking = useRef(false);
  const pollRef = useRef(null);

  useEffect(() => {
    const onSuspect = async () => {
      if (checking.current || pollRef.current) return;
      checking.current = true;
      const ok = await checkHealth();
      checking.current = false;
      if (ok) return;
      setDown(true);
      pollRef.current = setInterval(async () => {
        if (await checkHealth()) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setReconnected(true);
          setTimeout(() => { setDown(false); setReconnected(false); }, 1500);
        }
      }, 6000);
    };

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const isNetwork = !error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error');
        if (isNetwork || GATEWAY_CODES.includes(status)) onSuspect();
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  if (!down) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      data-testid="maintenance-overlay"
    >
      <div className="max-w-sm w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        {reconnected ? (
          <>
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Wieder verbunden ✅</h2>
            <p className="text-sm text-gray-500 mt-1">Es kann weitergehen!</p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Wrench className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Kurze Wartung 🔧</h2>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              Wir aktualisieren gerade das System. Das dauert in der Regel nur wenige
              Minuten — die Seite verbindet sich automatisch neu.
            </p>
            <div className="flex items-center justify-center gap-2 mt-5 text-xs text-gray-400">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Verbindung wird geprüft...
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MaintenanceOverlay;
